import React, { useCallback, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  DAY_LABELS,
  type IntervalType,
  buildCron,
  formatHour,
  parseSchedule,
} from './schedule-utils';

type SchedulePickerProps = {
  value: string;
  onChange: (cron: string) => void;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export const SchedulePicker: React.FC<SchedulePickerProps> = ({
  value,
  onChange,
}) => {
  const [intervalType, setIntervalType] = useState<IntervalType>(
    () => parseSchedule(value).intervalType
  );
  const [hour, setHour] = useState(() => parseSchedule(value).hour);
  const [dayOfWeek, setDayOfWeek] = useState(
    () => parseSchedule(value).dayOfWeek
  );

  const handleIntervalChange = useCallback(
    (newType: string) => {
      const t = newType as IntervalType;
      setIntervalType(t);
      if (t !== 'custom') {
        onChange(buildCron(t, hour, dayOfWeek));
      }
    },
    [hour, dayOfWeek, onChange]
  );

  const handleHourChange = useCallback(
    (hourStr: string) => {
      const h = parseInt(hourStr, 10);
      setHour(h);
      onChange(buildCron(intervalType, h, dayOfWeek));
    },
    [intervalType, dayOfWeek, onChange]
  );

  const handleDayChange = useCallback(
    (dayStr: string) => {
      const d = parseInt(dayStr, 10);
      setDayOfWeek(d);
      onChange(buildCron(intervalType, hour, d));
    },
    [intervalType, hour, onChange]
  );

  const handleCustomChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-sm font-medium text-slate-700 mb-1.5 block">
          Frequency
        </label>
        <Select value={intervalType} onValueChange={handleIntervalChange}>
          <SelectTrigger className="bg-white w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hourly">Every hour</SelectItem>
            <SelectItem value="daily">Every day</SelectItem>
            <SelectItem value="weekly">Every week</SelectItem>
            <SelectItem value="custom">Custom cron</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(intervalType === 'daily' || intervalType === 'weekly') && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
            Time (UTC)
          </label>
          <Select value={String(hour)} onValueChange={handleHourChange}>
            <SelectTrigger className="bg-white w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {formatHour(h)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {intervalType === 'weekly' && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
            Day
          </label>
          <Select value={String(dayOfWeek)} onValueChange={handleDayChange}>
            <SelectTrigger className="bg-white w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_LABELS.map((label, i) => (
                <SelectItem key={i} value={String(i)}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {intervalType === 'custom' && (
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
            Cron expression
          </label>
          <Input
            className="bg-white"
            value={value}
            onChange={handleCustomChange}
            placeholder="0 * * * *"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Format: minute hour day-of-month month day-of-week (UTC)
          </p>
        </div>
      )}
    </div>
  );
};
